import React, { useState, useEffect } from "react";
import { Table, Button, Alert, Form } from "react-bootstrap";
import ServiceCalls from "../utils/serviceCalls.js";
import CommonUtils from "../utils/siteUtils";
import { Loading } from "./Loading";
import { ConfirmModal } from "./ConfirmModal";

const formatSize = (bytes) => {
	if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
	if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${bytes} B`;
};

export const AdminPeopleSoftFiles = (props) => {
	const [folderOptions, setFolderOptions] = useState([]);
	const [folder, setFolder] = useState("main");
	const [files, setFiles] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [pendingDelete, setPendingDelete] = useState(null);
	const [statusMsg, setStatusMsg] = useState("");
	const [statusLevel, setStatusLevel] = useState("");

	useEffect(() => {
		ServiceCalls.getPeopleSoftFolders().then((data) => setFolderOptions(data || []));
	}, []);

	useEffect(() => {
		loadFiles(folder);
	}, [folder]);

	const loadFiles = async (selectedFolder) => {
		setIsLoading(true);
		props.onChildUpdate();
		const data = await ServiceCalls.getPeopleSoftFiles(selectedFolder);
		setFiles(data || []);
		setIsLoading(false);
	};

	const confirmDelete = async () => {
		const blobName = pendingDelete;
		setPendingDelete(null);
		const result = await ServiceCalls.deletePeopleSoftFile(blobName, folder);
		const success = result && result.isSuccessful;
		setStatusMsg(success ? `Deleted ${blobName}` : (result && result.errorMessage) || `Failed to delete ${blobName}`);
		setStatusLevel(success ? "success" : "danger");
		await loadFiles(folder);
	};

	const handleDownload = (blobName) => {
		ServiceCalls.downloadPeopleSoftFile(blobName, folder)
			.then((blob) => {
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", blobName);
				document.body.appendChild(link);
				link.click();
				link.remove();
			})
			.catch(() => {
				setStatusMsg(`Failed to download ${blobName}. Please try again, and contact IT support if the issue persists.`);
				setStatusLevel("danger");
			});
	};

	return (
		<div className="peopleSoftFilesArea">
			<div className="eftDetailText" style={{ marginBottom: "10px" }}>
				Select which storage location to view. The main folder holds files that are pending or currently being processed by the batch job; the archive folder holds files that have already been processed and are kept for history. Files in the archive folder can be downloaded but not deleted.
			</div>
			<Form.Group className="mb-3" style={{ width: "220px" }}>
				<Form.Label>Folder</Form.Label>
				<Form.Select value={folder} onChange={(e) => setFolder(e.target.value)}>
					{folderOptions.map((opt) => (
						<option key={opt.key} value={opt.key}>
							{opt.containerName}
						</option>
					))}
				</Form.Select>
			</Form.Group>
			<div style={{ marginBottom: "8px", color: "#888", fontSize: "12px" }}>Showing the most recent {files.length < 25 ? files.length : 25} files.</div>
			{statusMsg && (
				<Alert variant={statusLevel} dismissible onClose={() => setStatusMsg("")}>
					{statusMsg}
				</Alert>
			)}
			{isLoading ? (
				<Loading />
			) : files.length === 0 ? (
				<div>No PeopleSoft files are currently in the storage container.</div>
			) : (
				<Table striped bordered size="sm">
					<thead>
						<tr>
							<th>File</th>
							<th>Type</th>
							<th>Batch Run Id</th>
							<th>Size</th>
							<th>Modified</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{files.map((f) => (
							<tr key={f.name}>
								<td>{f.name}</td>
								<td>{f.fileType || "Unknown"}</td>
								<td>{f.batchRunId || "—"}</td>
								<td>{formatSize(f.sizeBytes)}</td>
								<td>{f.lastModifiedUtc ? CommonUtils.formatDate(f.lastModifiedUtc, true) : ""}</td>
								<td>
									<Button variant="link" onClick={() => handleDownload(f.name)}>
										Download
									</Button>
									{folder !== "archive" && (
										<Button variant="link" className="text-danger" onClick={() => setPendingDelete(f.name)}>
											Delete
										</Button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			)}
			<ConfirmModal
				isOpen={!!pendingDelete}
				message={`Are you sure you want to delete ${pendingDelete}?`}
				handleAccept={confirmDelete}
				handleClose={() => setPendingDelete(null)}
			/>
		</div>
	);
};
